'use client';

import {
	AccordionRoot,
	DrawerBackdrop,
	DrawerBody,
	DrawerCloseTrigger,
	DrawerContent,
	DrawerHeader,
	DrawerRoot,
	DrawerTrigger,
	Link,
} from '@packages/chakra-v3';
import {
	Box,
	IconButton,
	Stack,
	Text,
	useDisclosure,
} from '@chakra-ui/react';
import {
	NativeSelectField,
	NativeSelectRoot,
} from '@packages/chakra-v3';
import DrawerMenuAccordionItem from './DrawerMenuAccordionItem/DrawerMenuAccordionItem';
import { FaBars } from 'react-icons/fa';
import { useLocale } from 'next-intl';
import type { LocalizedText, NavigationMenuItemType } from '@/modules/sanity-studio/types';
import { setUserLocale } from '@/utils/locale';
import type { Locale } from '@/libs/next-intl';
import { useAutoComplete } from '@/modules/navigation/application';
import { Autocomplete } from '@/modules/shared';
import { APP_ROUTES } from '@/routes';
import { useRouter } from 'next/navigation';

type Link = {
	label: string;
	href:  string;
};

type DrawerMenuProps = {
	title:         LocalizedText;
	drawMenuItems: NavigationMenuItemType[];
};

const DrawerMenu = ({ title, drawMenuItems }: DrawerMenuProps) => {
	const locale = useLocale() as 'en' | 'th';
	const { open, onToggle, onClose } = useDisclosure();
	const handleChangeLocale = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const locale = e.target.value as Locale;
		setUserLocale(locale);
	};
	const autocomplete = useAutoComplete();
	const router = useRouter();

	const getLabel = (titleObj: NavigationMenuItemType['title']) => titleObj?.[locale] || Object.values(titleObj)?.[0] || '';
	const handleOnSelect = (collectionSlug: string, slug: string) => {
		const url = APP_ROUTES.SHOP.PRODUCT_DETAILS.replace(':collectionSlug', collectionSlug).replace(':productSlug', slug);
		router.push(url);
	};

	return (
		<DrawerRoot open={open} placement={'start'} onOpenChange={onToggle}>
			<DrawerBackdrop />
			<DrawerTrigger asChild>
				<IconButton
					aria-label={'Open menu'}
					variant={'ghost'}
				>
					<FaBars />
				</IconButton>
			</DrawerTrigger>
			<DrawerContent>
				<DrawerCloseTrigger />
				<DrawerHeader borderBottomWidth={'1px'} p={4}>
					<Text textStyle={'s3'}>{title[locale]}</Text>
				</DrawerHeader>
				<DrawerBody
					display={'flex'}
					flexDirection={'column'}
					h={'100%'}
					justifyContent={'space-between'}
				>
					<Stack gap={4} mt={8} >
						<Box hideFrom={'md'}>
							<Autocomplete
								bg={'white'}
								hook={autocomplete as never}
								placeholder={'search product'}
								size={'xs'}
								onSelectSuggestion={handleOnSelect}
							/>
						</Box>
						<AccordionRoot multiple textStyle={'p2'}>
							{drawMenuItems.map((menu) => {
								return menu.children?.length
									? (
										<DrawerMenuAccordionItem
											key={getLabel(menu.title)}
											menu={menu}
											onClick={onClose}
										/>
									)
									: (
										<Box key={getLabel(menu.title)} mt={4} textStyle={'s6'}>
											<Link href={menu.href || '#'} onClick={onClose}>
												{getLabel(menu.title)}
											</Link>
										</Box>
									);
							})}
						</AccordionRoot>
					</Stack>
					<NativeSelectRoot hideFrom={'md'} w={'100%'}>
						<NativeSelectField defaultValue={locale} onChange={handleChangeLocale}>
							<option value={'en'}>EN</option>
							<option value={'th'}>TH</option>
						</NativeSelectField>
					</NativeSelectRoot>
				</DrawerBody>
			</DrawerContent>
		</DrawerRoot>
	);
};

export default DrawerMenu;